import { Button, DialogFooter } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import type { IntegrationName } from "@tellescope/tanstack-query";
import { StepWrapper } from "./StepWrapper";
import { HealthieAppointmentTypeTable } from "./HealthieAppointmentTypeTable";
import { useHealthieAppointmentTypeImport } from "./useHealthieAppointmentTypeImport";

export interface HealthieAppointmentTypeSelectionStepProps {
  selectedIntegrationNames: IntegrationName[];
  onSkip?: () => void | Promise<void>;
  onImportSuccess: () => void | Promise<void>;
}

export const HealthieAppointmentTypeSelectionStep = ({
  selectedIntegrationNames,
  onSkip,
  onImportSuccess,
}: HealthieAppointmentTypeSelectionStepProps) => {
  const { t } = useTranslation("onboarding");
  const {
    appointmentTypes,
    isLoading,
    isError,
    allAlreadyImported,
    isImporting,
    resolvedSelectedAppointmentIds,
    allSelected,
    hasSelection,
    isAdmin,
    canImport,
    toggleAppointmentType,
    setAllSelected,
    importSelected,
  } = useHealthieAppointmentTypeImport({ onImportSuccess });

  return (
    <>
      <StepWrapper selectedIntegrationNames={selectedIntegrationNames} highlightedIntegrations={["healthie"]}>
        <div className="min-h-0 pt-6 pb-4 flex flex-col gap-6" data-testid="onboarding-healthie-templates-step">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-gray-900">{t("Healthie Integration Setup (1 of 2)")}</h3>
            <p className="text-sm text-gray-600">
              {t("Select the appointment types you'd like to sync from Healthie")}
            </p>
          </div>
          <HealthieAppointmentTypeTable
            appointmentTypes={appointmentTypes}
            isLoading={isLoading}
            isError={isError}
            allAlreadyImported={allAlreadyImported}
            selectedAppointmentTypeIds={resolvedSelectedAppointmentIds}
            allSelected={allSelected}
            hasSelection={hasSelection}
            onToggleAppointmentType={toggleAppointmentType}
            onToggleAll={setAllSelected}
          />
        </div>
      </StepWrapper>
      <DialogFooter className="bg-gray-50 flex items-center justify-end py-4 px-8">
        {!!onSkip && (
          <Button variant="outline" onClick={onSkip} disabled={!isAdmin} data-testid="onboarding-step-skip">
            {t("I'll do this later")}
          </Button>
        )}
        <Button
          data-testid="onboarding-step-continue"
          onClick={importSelected}
          loading={isImporting}
          disabled={!canImport}
        >
          {t("Continue")}
        </Button>
      </DialogFooter>
    </>
  );
};
